import {
  CareerPath,
  CareerCategory,
  AccordionDropdown,
  AccordionDropdownSxOverrides,
  AppIcon,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  getCareerPathsForCategory,
  getCategoryIcon,
  getCategoryDescription,
} from '@backrs_ui/grow-ui/src/pages/CareerPath/util';

import { Box, Typography, SxProps, Theme } from '@mui/material';
import ReactMarkdown from 'react-markdown';

const STRINGS = appStrings.modules.careerPath.summaryPage.whatIsACareerPathModal;

function Header({ onClose }: { onClose: () => void }) {
  return (
    <Box sx={headerContainerSx}>
      <Box sx={headerTextSx}>
        <Typography
          variant="display-md-semibold"
          color={(theme: Theme) => theme.backrs.colors['Colors/Base/White']}
        >
          {STRINGS.header.title}
        </Typography>
        <Typography
          variant="text-lg-medium"
          color={(theme: Theme) => theme.backrs.colors['Colors/Base/White']}
          sx={{
            display: {
              xs: 'none',
              md: 'block',
            },
          }}
        >
          {STRINGS.header.subtitle}
        </Typography>
      </Box>
      <Box id="career-path-modal-close-button" sx={closeIconContainerSx} onClick={onClose}>
        <AppIcon diameter="24px" color="Colors/Base/White" iconName="XClose" />
      </Box>
    </Box>
  );
}

function TopLeftSection() {
  return (
    <Box sx={topLeftSectionContainerSx}>
      <Box sx={sectionHeaderSx}>
        {/* title */}
        <Typography variant="display-sm-bold">{STRINGS.topLeftSection.title}</Typography>
        {/* image */}
        <Box
          sx={topLeftSectionImageSx}
          component="img"
          src="/images/stickers/apps-and-software.svg"
        />
      </Box>

      <Box>
        {/* description */}
        <Typography variant="text-sm-regular">
          <ReactMarkdown>{STRINGS.topLeftSection.description}</ReactMarkdown>
        </Typography>
      </Box>
    </Box>
  );
}

function TopRightSection({
  careerPaths,
  careerCategories,
}: {
  careerPaths: CareerPath[];
  careerCategories: CareerCategory[];
}) {
  return (
    <Box sx={topRightSectionContainerSx}>
      <Box sx={accordionContainerStyle}>
        {careerCategories.map((category) => {
          const categoryPaths = getCareerPathsForCategory(category.id, careerPaths);
          const subtitleItems = categoryPaths.map((path) => path.name);

          return (
            <Box key={category.id} sx={accordionWrapperStyle}>
              <AccordionDropdown
                id={`modal-career-category-accordion-${category.slug}`}
                key={category.id}
                title={category.short_name}
                iconName={getCategoryIcon(category.slug)}
                subtitles={subtitleItems}
                iconColor="Colors/Base/White"
                iconDiameter="14px"
                sxOverrides={careerPathAccordionSx}
                tooltipText={getCategoryDescription(category.slug)}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function BottomLeftSection() {
  return (
    <Box sx={bottomLeftSectionContainerSx}>
      <Box
        component="img"
        src="/images/static-career-path-tracks.svg"
        sx={bottomLeftSectionImageSx}
      />
    </Box>
  );
}

function BottomRightSection() {
  return (
    <Box sx={bottomRightSectionContainerSx}>
      <Box sx={sectionHeaderSx}>
        <Typography variant="display-sm-bold">{STRINGS.bottomRightSection.title}</Typography>
      </Box>
      <Box>
        <Typography variant="text-sm-regular">
          <ReactMarkdown>{STRINGS.bottomRightSection.description}</ReactMarkdown>
        </Typography>
      </Box>
    </Box>
  );
}

type WhatIsACareerPathModalContentProps = Readonly<{
  onClose: () => void;
  careerPaths: CareerPath[];
  careerCategories: CareerCategory[];
}>;

export function WhatIsACareerPathModalContent({
  onClose,
  careerPaths,
  careerCategories,
}: WhatIsACareerPathModalContentProps) {
  return (
    <Box sx={containerSx}>
      {/* header */}
      <Header onClose={onClose} />

      <Box sx={topSectionContainerSx}>
        {/* top left section */}
        <TopLeftSection />

        {/* top right section */}
        <TopRightSection careerPaths={careerPaths} careerCategories={careerCategories} />
      </Box>

      <Box sx={bottomSectionContainerSx}>
        {/* bottom left section */}
        <BottomLeftSection />

        {/* bottom right section */}
        <BottomRightSection />
      </Box>
    </Box>
  );
}

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '100vw',
  overflowY: 'auto',
  overflowX: 'clip',
};

const headerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  backgroundColor: backrs.colors['Colors/Base/Black'],
  justifyContent: 'space-between',
  padding: `${backrs.spacing['spacing-2xl']} ${backrs.spacing['spacing-3xl']}`,
  borderTopLeftRadius: backrs.radius['radius-lg'],
  borderTopRightRadius: backrs.radius['radius-lg'],
});

const headerTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const closeIconContainerSx: SxProps<Theme> = {
  cursor: 'pointer',
};

const topSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: '1fr 2fr',
  },
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderTop: 'none',
  borderBottom: {
    xs: 'none',
    md: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  },
});

const topLeftSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  padding: ({ backrs }) => backrs.spacing['spacing-5xl'],
  borderRight: {
    xs: 'none',
    md: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  },
  borderBottom: {
    xs: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    md: 'none',
  },
});

const topLeftSectionImageSx: SxProps<Theme> = {
  '@media (max-width: 450px)': {
    width: '90%',
    height: 'auto',
  },
  '@media (max-width: 400px)': {
    width: '80%',
    height: 'auto',
  },
  '@media (max-width: 380px)': {
    width: '35%',
  },
};

const sectionHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: {
    xs: backrs.spacing['spacing-md'],
    md: backrs.spacing['spacing-lg'],
  },
});

const topRightSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Brand/100'],
  padding: {
    xs: backrs.spacing['spacing-xl'],
    md: backrs.spacing['spacing-3xl'],
  },
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  overflow: 'hidden',
  borderBottom: {
    xs: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    md: 'none',
  },
});

const bottomSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: '1fr 1fr',
  },
  border: {
    xs: 'none',
    md: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  },
  borderTop: 'none',
  borderBottomLeftRadius: backrs.radius['radius-lg'],
  borderBottomRightRadius: backrs.radius['radius-lg'],
});

const bottomLeftSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  backgroundColor: backrs.colors['Brand/100'],
  borderRight: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  order: {
    xs: 2,
    md: 1,
  },
});

const bottomLeftSectionImageSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
  backgroundColor: backrs.colors['Brand/100'],
});

const bottomRightSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: backrs.colors['Colors/Base/White'],
  padding: ({ backrs }) => backrs.spacing['spacing-5xl'],
  gap: backrs.spacing['spacing-md'],
  order: 1,
  borderBottomRightRadius: {
    md: backrs.radius['radius-lg'],
  },
  borderBottom: {
    xs: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    md: 'none',
  },
});

const careerPathAccordionSx: AccordionDropdownSxOverrides = {
  container: ({ backrs }) => ({
    borderRadius: `${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-none']} ${backrs.spacing['spacing-none']}`,
  }),
  header: ({ backrs }) => ({
    padding: backrs.spacing['spacing-sm'],
    paddingRight: backrs.spacing['spacing-lg'],
    height: '40px',
    border: `0.5px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
    '&.Mui-expanded': {
      height: '40px !important',
      borderRadius: `${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-none']} ${backrs.spacing['spacing-none']}`,
    },
    borderRadius: `${backrs.spacing['spacing-sm']} ${backrs.spacing['spacing-sm']} ${backrs.spacing['spacing-none']} ${backrs.spacing['spacing-none']}`,
  }),
  iconContainer: ({ backrs }) => ({
    width: backrs.spacing['spacing-3xl'],
    height: backrs.spacing['spacing-3xl'],
    padding: backrs.spacing['spacing-xxs'],
    backgroundColor: backrs.colors['Brand/600'],
    borderRadius: backrs.spacing['spacing-sm'],
    alignItems: 'center',
    border: `0.5px solid ${backrs.colors['Beige/950']}`,
  }),
  content: ({ backrs }) => ({
    border: ` 0.5px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
    borderTop: 'none',
    borderRadius: `0 0 ${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-lg']}`,
  }),
  listItem: ({ backrs }) => ({
    '&:hover': {
      backgroundColor: backrs.colors['Colors/Base/Transparent'],
    },
    cursor: 'default',
  }),
  listItemText: ({ backrs }) => ({
    '& .MuiListItemText-primary': {
      fontSize: backrs.spacing['spacing-lg'],
    },
  }),
  title: ({ backrs, typography }) => ({
    ...typography['text-sm-semibold'],
    color: backrs.colors['Gray/700'],
  }),
};

const accordionContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(2, 1fr)',
    sm: 'repeat(3, 1fr)',
  },
  gap: {
    xs: backrs.spacing['spacing-md'],
    sm: backrs.spacing['spacing-xl'],
  },
  '@media (min-width: 900px) and (max-width: 975px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
    margin: backrs.spacing['spacing-xl'],
  },
  '@media (max-width: 450px)': {
    gridTemplateColumns: 'repeat(1, 1fr)',
    margin: 'auto',
  },
});

const accordionWrapperStyle: SxProps<Theme> = ({ backrs }) => ({
  '@media (max-width: 400px)': {
    '& .MuiAccordion-root': {
      minWidth: 'unset',
    },
    '& .MuiAccordionSummary-root': {
      padding: backrs.spacing['spacing-lg'],
      minHeight: 'unset',
      height: 'auto',
    },
    '& .MuiAccordionSummary-content': {
      margin: 0,
    },
    '& .MuiTypography-root': {
      fontSize: '14px',
      lineHeight: '1.2',
    },
    '& .MuiListItem-root': {
      padding: `${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-none']}`,
    },
    '& .MuiListItemText-primary': {
      fontSize: '13px',
      lineHeight: '1.2',
    },
  },
});
